/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { Alert, Box, Card, Typography } from '@mui/material';

import { CIColors } from 'shared';

import { HUModal2 } from '../../../../components/HUModal2/HUModal2';
import { Namespaces } from '../../../../i18n';
import { fetchJiraRequestTypes } from '../../../../utils/queryFns';
import { getLabelKey } from '../utils';

const CenteredBox = memo<PropsWithChildren>(({ children }) => (
  <Box style={{ display: 'flex', justifyContent: 'center' }}>{children}</Box>
));

interface Props {
  onClose: () => void;
  setRequestType: ({ id, name }: { id: string; name: string }) => void;
}

export const ChooseTicketType = memo<Props>(({ setRequestType, onClose }) => {
  const { t } = useTranslation([Namespaces.Common, Namespaces.General]);

  const {
    data: requestTypes,
    isLoading: isLoadingTypes,
    isError: isErrorTypes,
  } = useQuery([fetchJiraRequestTypes.key], fetchJiraRequestTypes.fn, {
    refetchOnMount: true,
  });

  return (
    <HUModal2
      open
      size="large"
      onCancel={onClose}
      disableRestoreFocus
      title={t(getLabelKey('chooseTicketType', true))}
      cancelButtonText={t(getLabelKey('cancel', true))}
      okButtonText={t(getLabelKey('chooseTicketType', true))}
    >
      <Box>
        {isLoadingTypes ? (
          <CenteredBox>
            <Typography>{t(getLabelKey('fetchingRequestTypes'))}</Typography>
          </CenteredBox>
        ) : isErrorTypes ? (
          <CenteredBox>
            <Alert severity="error">{t(getLabelKey('errorFetchingRequestTypes'))}</Alert>
          </CenteredBox>
        ) : !requestTypes || !requestTypes?.payload.length ? (
          <CenteredBox>
            <Typography>{t(getLabelKey('noRequestTypes'))}</Typography>
          </CenteredBox>
        ) : (
          <Box
            style={{
              display: 'flex',
              gap: '8px',
              rowGap: '16px',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {requestTypes.payload.map(requestType => (
              <Card
                key={requestType.id}
                onClick={() =>
                  setRequestType({
                    id: requestType.id,
                    name: requestType.name,
                  })
                }
                style={{
                  width: '32%',
                  borderRadius: '12px',
                  boxShadow: 'none',
                  backgroundColor: '#F6F7F8',
                  padding: '16px',
                  cursor: 'pointer',

                  display: 'flex',
                  gap: '8px',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <img src={requestType.icon} style={{ height: '32px' }} alt="request type icon" />
                <Box>
                  <Typography
                    style={{
                      fontSize: '16px',
                      lineHeight: '24px',
                      fontWeight: 'bold',
                      color: CIColors.Primary,
                    }}
                  >
                    {requestType.name}
                  </Typography>
                  {requestType.description ? (
                    <Typography
                      style={{
                        fontSize: '12px',
                        lineHeight: '16px',
                        fontWeight: 400,
                      }}
                    >
                      {requestType.description}
                    </Typography>
                  ) : null}
                </Box>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    </HUModal2>
  );
});
